import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import t from '../../i18n/intl';
import styled from 'styled-components';
import { Dimensions } from '../../../components/visuals';
import Textfield from '@atlaskit/textfield';
import { fetchCartByOrderNumber } from '../duck/actions/cart';
import { selectCartOrder } from '../duck/selectors';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import { colors } from '@atlaskit/theme';
import config from '../../../appconfig';
import { messages } from '../messages';
import { State } from 'model/State';
import {
  DescriptionLabel,
  ErrorBody,
  FormGroup,
  FormLabel,
  LabelWrapper,
  LookRowContainer,
  LookupErrorBanner,
  SubmitButton,
  TextfieldWrapper,
} from './styledComponents';
import { PartnerPortalLink } from '../../../util/constants';
import { MessageDescriptor } from 'util/lib/types';

const OrdersTitle = styled.h3`
  margin-top: ${Dimensions.units(5)}px;
  margin-bottom: ${Dimensions.units(1.5)}px;
  font-weight: 500;
  font-size: ${Dimensions.units(2)}px;
`;

function isNumericString(value: string): boolean {
  const normalizedValue = value.replace(',', '.');
  const numericRegex = /^-?\d+(\.\d+)?$/;
  return numericRegex.test(normalizedValue) && !Number.isNaN(parseFloat(normalizedValue));
}

const doesOrderMatchEmail = (email: string, cartForOrder: any): boolean =>
  cartForOrder?.cartOrderDetails?.billingContactEmail === email ||
  cartForOrder?.cartOrderDetails?.technicalContactEmail === email ||
  cartForOrder?.billingContact?.email === email ||
  cartForOrder?.technicalContact?.email === email;

type Props = {
  isPartner?: boolean;
};

const OrderLookup = ({ isPartner }: Props) => {
  const dispatch = useDispatch();

  const [errorMessage, setErrorMessage] = useState<MessageDescriptor>({ id: '' });
  const [isSubmitted, setIsSubmitted] = useState(0);
  const [formData, setFormData] = useState({
    orderNumber: '',
    total: '',
    email: '',
  });

  const cartForOrder = useSelector((state) =>
    selectCartOrder(+formData.orderNumber.replace('AT-', ''), state as State),
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value.replaceAll(' ', ''),
    });
  };
  const handleSubmit = async () => {
    if (!formData.orderNumber || !formData.total || !formData.email) {
      setErrorMessage(messages.ordersLookupFieldsNotCompleted);
      return;
    }

    if (!isNumericString(formData.total)) {
      setErrorMessage(messages.ordersLookupFieldsNotNumber);
      return;
    }
    dispatch(fetchCartByOrderNumber(+formData.orderNumber.replace('AT-', '')));
    setIsSubmitted(isSubmitted + 1);
  };

  useEffect(() => {
    if (cartForOrder !== null && isSubmitted) {
      setErrorMessage({ id: '' });

      if (!cartForOrder) {
        if (isPartner) setErrorMessage(messages.partnerOrderNotFound);
        else setErrorMessage(messages.orderNotFound);
        return;
      }
      const { email } = formData;
      if (
        !doesOrderMatchEmail(email, cartForOrder) ||
        cartForOrder?.cartOrderDetails?.totalIncTax !== parseFloat(formData.total)
      ) {
        setErrorMessage(messages.orderMatchError);
        return;
      }

      const orderId = formData.orderNumber.replace('AT-', '');
      if (cartForOrder.status === 'PROCESSED' || cartForOrder.status === 'ORDER_PLACED') {
        window.location.href = `/purchase/my/invoices/${orderId}`;
      } else {
        window.location.href = `/purchase/my/orders/${orderId}`;
      }
    }
  }, [cartForOrder, isSubmitted]);

  return (
    <>
      <OrdersTitle> {t('mac.orders.lookup.title')} </OrdersTitle>
      <div style={{ color: `${colors.N300}` }}>
        <FormattedMessage {...messages.ordersLookupInfoText} />
      </div>
      {errorMessage?.id && (
        <LookupErrorBanner>
          <ErrorBody data-cy="lookup-error-message">
            <ErrorIcon primaryColor={colors.R400} label="lookup-error-message-icon" />
            <span style={{ padding: `${Dimensions.units(2.25)}px ${Dimensions.units(1.25)}px` }}>
              <FormattedMessage
                {...errorMessage}
                values={{
                  ppcLink: (
                    <a href={config.ppcUrl} target="_blank" rel="noopener noreferrer">
                      {t('mac.orders.lookup.error.ppc')}
                    </a>
                  ),
                  partnerPortalLink: (
                    <a href={PartnerPortalLink} target="_blank" rel="noopener noreferrer">
                      {t('mac.orders.lookup.error.partner.portal')}
                    </a>
                  ),
                  faqLink: (
                    <a
                      href={config.wacPurchaseLicensingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('mac.orders.lookup.error.faq')}
                    </a>
                  ),
                  contactLink: (
                    <a href={config.companyContact} target="_blank" rel="noopener noreferrer">
                      {t('mac.orders.lookup.error.contact-forms')}
                    </a>
                  ),
                }}
              />
            </span>
          </ErrorBody>
        </LookupErrorBanner>
      )}
      <FormGroup>
        <LookRowContainer>
          <LabelWrapper>
            <FormLabel htmlFor="orderNumber">{t('mac.orders.lookup.input.order.label')}</FormLabel>
          </LabelWrapper>
          <TextfieldWrapper>
            <Textfield
              data-cy="order-lookup-orderid"
              style={{ height: `${Dimensions.units(3.75)}px` }}
              id="orderNumber"
              value={formData.orderNumber}
              name="orderNumber"
              onChange={handleChange}
            />
            <DescriptionLabel htmlFor="orderEmail">
              {t('mac.orders.lookup.input.order.hint')}
            </DescriptionLabel>
          </TextfieldWrapper>
        </LookRowContainer>
        <LookRowContainer>
          <LabelWrapper>
            <FormLabel htmlFor="orderTotal">{t('mac.orders.lookup.input.total.label')}</FormLabel>
          </LabelWrapper>
          <TextfieldWrapper>
            <Textfield
              data-cy="order-lookup-total"
              style={{ height: `${Dimensions.units(3.75)}px` }}
              id="orderTotal"
              value={formData.total}
              name="total"
              onChange={handleChange}
            />
            <DescriptionLabel htmlFor="orderEmail">
              {t('mac.orders.lookup.input.total.hint')}
            </DescriptionLabel>
          </TextfieldWrapper>
        </LookRowContainer>
        <LookRowContainer>
          <LabelWrapper>
            <FormLabel htmlFor="orderEmail">{t('mac.orders.lookup.input.email.label')}</FormLabel>
          </LabelWrapper>
          <TextfieldWrapper>
            <Textfield
              data-cy="order-lookup-email"
              style={{ height: `${Dimensions.units(3.75)}px` }}
              type="orderEmail"
              value={formData.email}
              name="email"
              onChange={handleChange}
            />
            <DescriptionLabel htmlFor="orderEmail">
              {t('mac.orders.lookup.input.email.hint')}
            </DescriptionLabel>
          </TextfieldWrapper>
        </LookRowContainer>
        <div>
          <SubmitButton
            data-cy="order-lookup-btn"
            appearance="primary"
            onClick={handleSubmit}
            type="submit"
          >
            <FormattedMessage {...messages.lookupButton} />
          </SubmitButton>
        </div>
      </FormGroup>
    </>
  );
};

export default OrderLookup;
